/* Ürün sayfası bileşenleri: kaydırıcı, galeri, varyant/sepet, videolar, önce-sonra. */
(() => {
  const define = (name, component) => { if (!customElements.get(name)) customElements.define(name, component); };

  /* ---------- Genel kaydırıcı (scroll-snap tabanlı) ---------- */
  class PPSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;
      this.abort = new AbortController();
      const opts = { signal: this.abort.signal };
      this.loop = this.dataset.loop === 'true';
      this.dots = this.querySelector('[data-dots]');
      this.querySelectorAll('[data-prev]').forEach(b => b.addEventListener('click', () => this.step(-1), opts));
      this.querySelectorAll('[data-next]').forEach(b => b.addEventListener('click', () => this.step(1), opts));
      let frame = 0;
      this.track.addEventListener('scroll', () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(() => this.sync()); }, { ...opts, passive: true });
      this.resizeObserver = new ResizeObserver(() => this.build());
      this.resizeObserver.observe(this.track);
      this.enableDrag(opts);
      if (this.dataset.autoplay === 'true') {
        const pause = () => { this.paused = true; }, resume = () => { this.paused = false; };
        this.addEventListener('mouseenter', pause, opts); this.addEventListener('mouseleave', resume, opts);
        this.addEventListener('focusin', pause, opts); this.addEventListener('focusout', resume, opts);
        const delay = Math.max(2, Number(this.dataset.speed) || 5) * 1000;
        this.timer = setInterval(() => { if (!this.paused && !document.hidden) this.step(1); }, delay);
      }
      document.addEventListener('shopify:block:select', e => { const i = this.slides.findIndex(s => s.contains(e.target)); if (i >= 0) this.goTo(i); }, opts);
      this.build();
    }
    get slides() { return [...this.track.children].filter(el => el.offsetParent !== null || el.getClientRects().length); }
    perView() {
      const s = this.slides; if (s.length < 2) return 1;
      const step = s[1].offsetLeft - s[0].offsetLeft || s[0].offsetWidth || 1;
      return Math.max(1, Math.round((this.track.clientWidth + 1) / step));
    }
    pages() { return Math.max(1, this.slides.length - this.perView() + 1); }
    build() {
      const pages = this.pages();
      this.classList.toggle('is-static', pages <= 1);
      if (this.dots) {
        if (this.dots.children.length !== pages) {
          this.dots.replaceChildren();
          for (let i = 0; i < pages; i++) {
            const b = document.createElement('button');
            b.type = 'button';
            b.setAttribute('aria-label', `${i + 1}. slayta git`);
            b.addEventListener('click', () => this.goTo(i));
            this.dots.append(b);
          }
        }
        this.dots.hidden = pages <= 1;
      }
      this.sync();
    }
    current() {
      const s = this.slides; if (!s.length) return 0;
      const left = this.track.scrollLeft; let best = 0, dist = Infinity;
      s.forEach((slide, i) => { const d = Math.abs(slide.offsetLeft - s[0].offsetLeft - left); if (d < dist) { dist = d; best = i; } });
      const maxLeft = this.track.scrollWidth - this.track.clientWidth;
      if (left >= maxLeft - 2) best = this.pages() - 1;
      return Math.min(best, this.pages() - 1);
    }
    sync() {
      const i = this.current();
      if (this.dots) [...this.dots.children].forEach((d, n) => d.setAttribute('aria-current', String(n === i)));
      const atStart = this.track.scrollLeft <= 2, atEnd = this.track.scrollLeft >= this.track.scrollWidth - this.track.clientWidth - 2;
      if (!this.loop) {
        this.querySelectorAll('[data-prev]').forEach(b => b.disabled = atStart);
        this.querySelectorAll('[data-next]').forEach(b => b.disabled = atEnd);
      }
      if (i !== this.index) { this.index = i; this.dispatchEvent(new CustomEvent('pp:change', { detail: { index: i }, bubbles: true })); }
    }
    goTo(i, instant) {
      const s = this.slides; if (!s[i]) return;
      this.track.scrollTo({ left: s[i].offsetLeft - s[0].offsetLeft, behavior: instant ? 'auto' : 'smooth' });
    }
    step(dir) {
      const pages = this.pages(); let next = this.current() + dir;
      if (next < 0) next = this.loop ? pages - 1 : 0;
      if (next > pages - 1) next = this.loop ? 0 : pages - 1;
      this.goTo(next);
    }
    enableDrag(opts) {
      let startX = 0, startLeft = 0, startIndex = 0, dragging = false, moved = false;
      this.track.addEventListener('pointerdown', e => {
        if (e.pointerType !== 'mouse' || e.button !== 0 || e.target.closest('button, a, input, select, video[controls], model-viewer')) return;
        dragging = true; moved = false; startX = e.clientX; startLeft = this.track.scrollLeft; startIndex = this.current();
        this.track.classList.add('is-dragging');
      }, opts);
      window.addEventListener('pointermove', e => {
        if (!dragging) return;
        const dx = e.clientX - startX; if (Math.abs(dx) > 4) moved = true;
        this.track.scrollLeft = startLeft - dx;
      }, opts);
      window.addEventListener('pointerup', () => {
        if (!dragging) return; dragging = false; this.track.classList.remove('is-dragging');
        if (moved) {
          const dx = this.track.scrollLeft - startLeft, pages = this.pages();
          let i = startIndex;
          if (dx > 40) i = startIndex + 1; else if (dx < -40) i = startIndex - 1;
          this.goTo(Math.min(pages - 1, Math.max(0, i)));
        }
      }, opts);
      this.track.addEventListener('click', e => { if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; } }, { ...opts, capture: true });
    }
    disconnectedCallback() { clearInterval(this.timer); this.resizeObserver?.disconnect(); this.abort?.abort(); }
  }
  define('pp-slider', PPSlider);

  /* ---------- Ürün galerisi (küçük resimler + ana kaydırıcı) ---------- */
  class PPGallery extends HTMLElement {
    connectedCallback() {
      this.slider = this.querySelector('pp-slider');
      this.thumbs = [...this.querySelectorAll('[data-thumb]')];
      this.thumbTrack = this.querySelector('[data-thumbs]');
      this.abort = new AbortController();
      const opts = { signal: this.abort.signal };
      this.thumbs.forEach((t, i) => t.addEventListener('click', () => this.slider?.goTo(i), opts));
      this.addEventListener('pp:change', e => { if (e.target === this.slider) this.mark(e.detail.index); }, opts);
    }
    mark(index) {
      this.thumbs.forEach((t, i) => t.setAttribute('aria-current', String(i === index)));
      this.slider?.querySelectorAll('[data-track] > *').forEach((slide, i) => { if (i !== index) slide.querySelectorAll('video').forEach(v => v.pause()); });
      const t = this.thumbs[index];
      if (t && this.thumbTrack) {
        const left = t.offsetLeft - (this.thumbTrack.clientWidth - t.offsetWidth) / 2;
        this.thumbTrack.scrollTo({ left, behavior: 'smooth' });
      }
    }
    showMedia(mediaId, instant) {
      if (!this.slider || !mediaId) return;
      const slides = [...this.slider.querySelectorAll('[data-track] > [data-media-id]')];
      const i = slides.findIndex(s => s.dataset.mediaId === String(mediaId));
      if (i >= 0) { this.slider.goTo(i, instant); this.mark(i); }
    }
    disconnectedCallback() { this.abort?.abort(); }
  }
  define('pp-gallery', PPGallery);

  /* ---------- Ürün bilgisi: varyant seçici, fiyat, sepete ekle ---------- */
  class PPProduct extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-pp-variants]');
      if (!json) return;
      this.data = JSON.parse(json.textContent);
      this.form = this.querySelector('form[data-pp-form]');
      this.idSelect = this.form?.querySelector('[name="id"]');
      this.button = this.querySelector('[data-pp-add]');
      this.error = this.querySelector('[data-pp-error]');
      this.abort = new AbortController();
      const opts = { signal: this.abort.signal };
      this.querySelectorAll('[data-option-index]').forEach(input => input.addEventListener('change', () => this.onOptionChange(), opts));
      this.idSelect?.addEventListener('change', () => { this.variant = this.data.variants.find(v => String(v.id) === this.idSelect.value); this.render(true); }, opts);
      this.form?.addEventListener('submit', e => this.submit(e), opts);
      this.variant = this.data.variants.find(v => String(v.id) === String(this.idSelect?.value)) || this.data.variants[0];
      this.render(false);
      if (this.variant?.mediaId && new URLSearchParams(location.search).has('variant')) {
        requestAnimationFrame(() => this.querySelector('pp-gallery')?.showMedia(this.variant.mediaId, true));
      }
    }
    selectedOptions() {
      const values = [];
      this.querySelectorAll('[data-option-index]').forEach(input => {
        const i = Number(input.dataset.optionIndex);
        if (input.tagName === 'SELECT') values[i] = input.value;
        else if (input.checked) values[i] = input.value;
      });
      return values;
    }
    onOptionChange() {
      const values = this.selectedOptions();
      this.variant = this.data.variants.find(v => v.options.every((o, i) => values[i] === undefined || o === values[i]));
      this.render(true);
    }
    render(changed) {
      const v = this.variant;
      const values = v ? v.options : this.selectedOptions();
      this.querySelectorAll('[data-selected-value]').forEach(el => { el.textContent = values[Number(el.dataset.selectedValue)] || ''; });
      this.markAvailability(values);
      const price = this.querySelector('[data-pp-price]');
      if (!v) {
        if (this.button) { this.button.disabled = true; this.setLabel(this.data.texts.unavailable); }
        if (price) price.classList.add('is-unavailable');
        return;
      }
      if (this.idSelect) this.idSelect.value = String(v.id);
      if (price) {
        price.classList.remove('is-unavailable');
        price.classList.toggle('is-on-sale', Boolean(v.compare));
        price.classList.toggle('is-sold-out', !v.available);
        const cur = price.querySelector('[data-price-current]'), cmp = price.querySelector('[data-price-compare]'), badge = price.querySelector('[data-price-badge]');
        if (cur) cur.textContent = v.price;
        if (cmp) cmp.textContent = v.compare || '';
        if (badge && v.badge != null) badge.innerHTML = v.badge;
      }
      if (this.button) {
        this.button.disabled = !v.available;
        this.setLabel(v.available ? this.data.texts.add : this.data.texts.soldOut);
        const p = this.button.querySelector('[data-atc-price]'); if (p) { p.textContent = v.price + ' •'; p.hidden = !v.available; }
      }
      if (changed) {
        if (v.mediaId) this.querySelector('pp-gallery')?.showMedia(v.mediaId);
        const url = new URL(location.href); url.searchParams.set('variant', String(v.id)); history.replaceState({}, '', url);
      }
    }
    markAvailability(values) {
      this.querySelectorAll('[data-option-index]').forEach(input => {
        const i = Number(input.dataset.optionIndex);
        const check = value => this.data.variants.some(v => v.available && v.options[i] === value && v.options.every((o, n) => n === i || values[n] === undefined || o === values[n]));
        if (input.tagName === 'SELECT') [...input.options].forEach(o => { o.textContent = o.value + (check(o.value) ? '' : ' — ' + this.data.texts.soldOut); });
        else input.closest('.pp-pill')?.classList.toggle('is-unavailable', !check(input.value));
      });
    }
    setLabel(text) { const label = this.button?.querySelector('[data-atc-label]'); if (label) label.textContent = text; }
    async submit(event) {
      if (!window.fetch || !window.FormData) return;
      if (this.form.dataset.skipCart === 'true') return;
      event.preventDefault();
      if (this.busy || !this.variant?.available) return;
      this.busy = true; this.button.disabled = true; this.button.classList.add('is-loading');
      if (this.error) { this.error.textContent = ''; this.error.hidden = true; }
      try {
        const endpoint = new URL(this.form.action, location.origin);
        endpoint.pathname = endpoint.pathname.replace(/\/add\/?$/, '/add.js');
        const body = new FormData(this.form); body.delete('return_to');
        const response = await fetch(endpoint, { method: 'POST', body, headers: { Accept: 'application/json' }, credentials: 'same-origin' });
        const result = await response.json();
        if (!response.ok) throw new Error(typeof result.description === 'string' ? result.description : this.data.texts.error);
        location.assign(this.data.cartUrl);
      } catch (error) {
        if (this.error) { this.error.textContent = error.message || this.data.texts.error; this.error.hidden = false; }
        this.button.disabled = !this.variant?.available; this.button.classList.remove('is-loading'); this.busy = false;
      }
    }
    disconnectedCallback() { this.abort?.abort(); }
  }
  define('pp-product', PPProduct);

  /* ---------- Video oynatıcı (sessiz otomatik oynatma + oynat düğmesi) ----------
     Takılmayı azaltmak için: video ekrana yaklaşınca önceden yüklenir, yalnızca
     yeterince göründüğünde oynatılır, sekme arka plandayken durdurulur. */
  const allVideos = new Set();
  document.addEventListener('visibilitychange', () => {
    allVideos.forEach(v => { if (document.hidden) v.suspend(); else v.resume(); });
  });
  class PPVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      this.button = this.querySelector('[data-play]');
      if (!this.video) return;
      allVideos.add(this);
      this.abort = new AbortController();
      const opts = { signal: this.abort.signal };
      this.button?.addEventListener('click', () => this.toggle(), opts);
      this.video.addEventListener('play', () => this.classList.add('is-playing'), opts);
      this.video.addEventListener('pause', () => this.classList.remove('is-playing'), opts);
      let waitTimer = 0;
      this.video.addEventListener('waiting', () => { clearTimeout(waitTimer); waitTimer = setTimeout(() => this.classList.add('is-loading'), 400); }, opts);
      ['playing', 'canplay', 'pause'].forEach(t => this.video.addEventListener(t, () => { clearTimeout(waitTimer); this.classList.remove('is-loading'); }, opts));
      this.autoplay = this.dataset.autoplay === 'true' && !matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (this.autoplay && 'IntersectionObserver' in window) {
        this.preloader = new IntersectionObserver(entries => {
          if (entries.some(e => e.isIntersecting)) { this.load(); this.preloader.disconnect(); }
        }, { rootMargin: '400px 0px' });
        this.preloader.observe(this);
        this.observer = new IntersectionObserver(entries => entries.forEach(entry => {
          this.visible = entry.intersectionRatio >= 0.35;
          if (this.visible) { if (!this.userPaused && !document.hidden) this.play(); }
          else if (!entry.isIntersecting && !this.video.paused) this.video.pause();
        }), { threshold: [0, 0.35] });
        this.observer.observe(this);
      }
    }
    load() {
      if (this.loaded) return; this.loaded = true;
      let swapped = false;
      this.video.querySelectorAll('source[data-src]').forEach(s => { s.src = s.dataset.src; s.removeAttribute('data-src'); swapped = true; });
      if (swapped) { this.video.preload = 'auto'; this.video.load(); }
    }
    play() {
      this.load();
      if (!this.video.paused) return;
      const p = this.video.play();
      if (p) p.catch(() => this.classList.remove('is-playing'));
    }
    suspend() { if (!this.video.paused) { this.wasPlaying = true; this.video.pause(); } }
    resume() { if (this.wasPlaying && this.visible && !this.userPaused) this.play(); this.wasPlaying = false; }
    toggle() {
      if (this.video.paused) { this.userPaused = false; this.play(); }
      else { this.userPaused = true; this.video.pause(); }
    }
    disconnectedCallback() { allVideos.delete(this); this.preloader?.disconnect(); this.observer?.disconnect(); this.abort?.abort(); }
  }
  define('pp-video', PPVideo);

  /* ---------- Ekranda olmayan dalga animasyonlarını durdur (video oynarken ana iş parçacığını rahatlatır) ---------- */
  if ('IntersectionObserver' in window) {
    const waveObserver = new IntersectionObserver(entries => entries.forEach(e => e.target.classList.toggle('is-offscreen', !e.isIntersecting)));
    const watchWaves = () => document.querySelectorAll('.pp-waves:not([data-watched])').forEach(el => { el.dataset.watched = ''; waveObserver.observe(el); });
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watchWaves); else watchWaves();
    document.addEventListener('shopify:section:load', watchWaves);
  }

  /* ---------- Önce / Sonra karşılaştırma ---------- */
  class PPCompare extends HTMLElement {
    connectedCallback() {
      const input = this.querySelector('input[type="range"]');
      if (!input) return;
      const update = () => this.style.setProperty('--pos', input.value + '%');
      input.addEventListener('input', update); update();
    }
  }
  define('pp-compare', PPCompare);
})();
